'use client';

import React, { useRef, useState, useEffect } from 'react';

interface LazyLoadWrapperProps {
  children: React.ReactNode;
  /**
   * Defines a margin around the root element (viewport).
   * E.g., '0px 0px 200px 0px' means load when 200px from the bottom.
   */
  rootMargin?: string; 
}

/**
 * Renders its children only when they scroll into the viewport.
 */
const LazyLoadWrapper: React.FC<LazyLoadWrapperProps> = ({ 
  children, 
  rootMargin = '0px', 
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // If the component is already visible, stop observing
    if (isVisible) return; 
    
    // Check for IntersectionObserver support (for older browsers)
    if (!('IntersectionObserver' in window)) {
      // Fallback: render immediately if IO is not supported
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Once visible, stop observing to save resources
            observer.unobserve(entry.target); 
          }
        });
      },
      {
        root: null, // Default is the viewport
        rootMargin,
        threshold: 0.01, // Trigger when even 1% of the component is visible
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    // Cleanup: disconnect the observer when the component unmounts
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);
  
  // Render a placeholder (with defined height) or the actual content
  // Note: Set a placeholder height/min-height to prevent layout shift (CLS)
  return (
    <div ref={ref} style={{ minHeight: isVisible ? 'auto' : '300px' }}>
      {isVisible ? children : null}
    </div>
  );
};

export default LazyLoadWrapper;